/**
 * 高性能位操作类
 * 基于BigInt实现，支持任意数量的位操作
 */
export class Bits {
    constructor(initialValue = 0n) {
        this._value = 0n;
        this._value = initialValue;
    }
    /**
     * 设置指定位置的位为1
     */
    set(index) {
        if (index < 0) {
            throw new Error('Bit index cannot be negative');
        }
        this._value |= (1n << BigInt(index));
    }
    /**
     * 清除指定位置的位（设为0）
     */
    clear(index) {
        if (index < 0) {
            throw new Error('Bit index cannot be negative');
        }
        this._value &= ~(1n << BigInt(index));
    }
    /**
     * 获取指定位置的位值
     */
    get(index) {
        if (index < 0) {
            return false;
        }
        return (this._value & (1n << BigInt(index))) !== 0n;
    }
    /**
     * 检查是否包含所有指定的位
     */
    containsAll(other) {
        return (this._value & other._value) === other._value;
    }
    /**
     * 检查是否包含任意一个指定的位
     */
    intersects(other) {
        return (this._value & other._value) !== 0n;
    }
    /**
     * 检查是否不包含任何指定的位
     */
    excludes(other) {
        return !this.intersects(other);
    }
    /**
     * 清空所有位
     */
    clearAll() {
        this._value = 0n;
    }
    /**
     * 检查是否为空（没有设置任何位）
     */
    isEmpty() {
        return this._value === 0n;
    }
    /**
     * 获取设置的位数量
     */
    cardinality() {
        let count = 0;
        let value = this._value;
        while (value > 0n) {
            if (value & 1n) {
                count++;
            }
            value >>= 1n;
        }
        return count;
    }
    /**
     * 位运算：与
     */
    and(other) {
        return new Bits(this._value & other._value);
    }
    /**
     * 位运算：或
     */
    or(other) {
        return new Bits(this._value | other._value);
    }
    /**
     * 位运算：异或
     */
    xor(other) {
        return new Bits(this._value ^ other._value);
    }
    /**
     * 位运算：非
     */
    not(maxBits = 64) {
        const mask = (1n << BigInt(maxBits)) - 1n;
        return new Bits((~this._value) & mask);
    }
    /**
     * 复制另一个Bits对象
     */
    copyFrom(other) {
        this._value = other._value;
    }
    /**
     * 创建当前Bits的副本
     */
    clone() {
        return new Bits(this._value);
    }
    /**
     * 获取原始BigInt值
     */
    getValue() {
        return this._value;
    }
    /**
     * 设置原始BigInt值
     */
    setValue(value) {
        this._value = value;
    }
    /**
     * 获取调试信息
     */
    toString() {
        const bits = [];
        let index = 0;
        let value = this._value;
        while (value > 0n) {
            if (value & 1n) {
                bits.push(index.toString());
            }
            value >>= 1n;
            index++;
        }
        return `Bits[${bits.join(', ')}]`;
    }
    /**
     * 获取二进制表示
     */
    toBinaryString(maxBits = 64) {
        let result = '';
        for (let i = maxBits - 1; i >= 0; i--) {
            result += this.get(i) ? '1' : '0';
            if (i % 8 === 0 && i > 0) {
                result += ' ';
            }
        }
        return result;
    }
    /**
     * 获取十六进制表示
     */
    toHexString() {
        return '0x' + this._value.toString(16).toUpperCase();
    }
    /**
     * 从二进制字符串创建Bits
     */
    static fromBinaryString(binaryString) {
        const cleanString = binaryString.replace(/\s/g, '');
        const value = BigInt('0b' + cleanString);
        return new Bits(value);
    }
    /**
     * 从十六进制字符串创建Bits
     */
    static fromHexString(hexString) {
        const cleanString = hexString.replace(/^0x/i, '');
        const value = BigInt('0x' + cleanString);
        return new Bits(value);
    }
    /**
     * 比较两个Bits对象是否相等
     */
    equals(other) {
        return this._value === other._value;
    }
    /**
     * 获取最高位的索引
     */
    getHighestBitIndex() {
        if (this._value === 0n) {
            return -1;
        }
        let index = 0;
        let value = this._value;
        while (value > 1n) {
            value >>= 1n;
            index++;
        }
        return index;
    }
    /**
     * 获取最低位的索引
     */
    getLowestBitIndex() {
        if (this._value === 0n) {
            return -1;
        }
        let index = 0;
        let value = this._value;
        while ((value & 1n) === 0n) {
            value >>= 1n;
            index++;
        }
        return index;
    }
}
//# sourceMappingURL=Bits.js.map